<template>
  <Component :is="wrapperTag" ref="wrapper" :class="{ 'v-popper-wrapper': true, 'v-popper--active': active }" @mouseleave="onMouseleave" @mouseover="onMouseenter">
    <Component
      :is="tag"
      ref="reference"
      class="v-popper-reference"
      :class="referenceClass"
      :style="referenceStyle"
      :tabindex="trigger === 'focus' && !visible ? 0 : -1"
      @click="referenceClick"
      @focus="referenceFocus"
      @mousedown="referenceMousedown"
    >
      <slot name="reference" />
    </Component>

    <Transition v-if="transition && (!lazy || rendered)" appear :name="transition" @after-leave="destroyPopper">
      <Teleport :disabled="!mountTo" :to="mountTo">
        <div v-show="visible" ref="popper" class="v-popper" :class="popperClass" @mouseenter="$emit('popperMouseenter', $event)" @mouseleave="$emit('popperMouseleave', $event)">
          <div v-if="showArrow" class="v-popper__arrow" data-popper-arrow />

          <slot />
        </div>
      </Teleport>
    </Transition>

    <div
      v-else-if="!lazy || rendered"
      v-show="visible"
      ref="popper"
      class="v-popper"
      :class="popperClass"
      @mouseenter="$emit('popperMouseenter', $event)"
      @mouseleave="$emit('popperMouseleave', $event)"
    >
      <div v-if="showArrow" class="v-popper__arrow" data-popper-arrow />

      <slot />
    </div>
  </Component>
</template>

<script setup lang="ts">
import { createPopper, type Instance } from '@popperjs/core'
import { computed, nextTick, onBeforeUnmount, onDeactivated, ref, useTemplateRef, watch } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import type { VPopperPlacement, VPopperTrigger } from '../types.ts'
import { delay, nextFrame, targetInOneOfSelectors } from '../utils/helpers.ts'

interface Props {
  placement?: VPopperPlacement
  trigger?: VPopperTrigger
  tag?: string
  wrapperTag?: string
  offset?: number
  showArrow?: boolean
  lazy?: boolean
  closeOnClickOutside?: boolean
  closeOnClickReference?: boolean
  openDelay?: number
  transition?: string
  referenceStyle?: any
  referenceClass?: any
  popperClass?: any
  mountTo?: string | HTMLElement
  clickIgnoreOn?: string[]
  clickRegisterOn?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom',
  trigger: 'hover',
  tag: 'div',
  wrapperTag: 'div',
  offset: 0,
  showArrow: false,
  lazy: false,
  closeOnClickOutside: true,
  closeOnClickReference: true,
  openDelay: 0,
  transition: 'v-fade',
  referenceStyle: '',
  referenceClass: '',
  popperClass: '',
  mountTo: undefined,
  clickIgnoreOn: () => [],
  clickRegisterOn: () => [],
})

const emit = defineEmits<{
  popperMouseenter: [value: MouseEvent]
  popperMouseleave: [value: MouseEvent]
  destroyPopper: []
  hide: []
  show: []
}>()

defineSlots<{
  default?(): any
  reference?(): any
}>()

let popperInstance: Instance | undefined = undefined
const wrapper = useTemplateRef<HTMLElement>('wrapper')
const reference = useTemplateRef<HTMLElement>('reference')
const popper = useTemplateRef('popper')
const active = ref(false)
const visible = ref(false)
const hovering = ref(false)
const rendered = ref(!props.lazy)
const hasMouseDown = ref(false)
const id = counter++

const totalOffset = computed(() => (props.showArrow ? props.offset + 10 : props.offset))

const modifiers = computed(() => {
  const modifiers = [
    { name: 'computeStyles', options: { adaptive: false, gpuAcceleration: false, roundOffsets: false } },
    { name: 'offset', options: { offset: [0, totalOffset.value] } },
    { name: 'preventOverflow', options: { padding: 7 } },
  ]

  if (props.showArrow) {
    modifiers.push({ name: 'arrow', options: { padding: 5 } })
  }

  return modifiers
})

watch(
  () => visible.value,
  (isVisible) => {
    if (isVisible) {
      mouseupListeners[id] = onMouseup
    } else {
      delete mouseupListeners[id]
    }
  },
)

watch(
  () => props.lazy,
  () => {
    if (!props.lazy) {
      rendered.value = true
    }
  },
  { immediate: true },
)

function cleanup() {
  delete mouseupListeners[id]
  document.removeEventListener('keydown', onKeyDown)
  destroyPopper()
}
onDeactivated(() => cleanup())
onBeforeUnmount(() => {
  cleanup()
  if (popper.value?.parentNode) {
    popper.value.parentNode.removeChild(popper.value)
  }
})

async function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Tab' && props.trigger === 'focus') {
    await nextFrame()
    if (visible.value && !wrapper.value?.contains(document.activeElement) && !popper.value?.contains(document.activeElement)) {
      hide()
    }
  }
}

function onMouseup(event: CustomMouseEvent) {
  hasMouseDown.value = false
  const target = event.target as HTMLElement

  if (wrapper.value && !wrapper.value.contains(target) && !popper.value?.contains(target)) {
    onClickOutside(event)
  }
}

function onClickOutside(event: CustomMouseEvent) {
  if (props.closeOnClickOutside && visible.value && (props.trigger === 'click' || props.trigger === 'focus')) {
    hide()
    event.isStopped = true
  }
}

function referenceFocus() {
  // The focus event is also triggered after the user has pressed their mouse down.
  // We want to ignore the focus event when this happens.
  if (props.trigger === 'focus' && !hasMouseDown.value) {
    show()
  }
}

function referenceMousedown() {
  hasMouseDown.value = true
}

function referenceClick({ target }: MouseEvent) {
  if (props.trigger === 'click' || props.trigger === 'focus') {
    if (visible.value && props.closeOnClickReference && (props.trigger !== 'focus' || !hasMouseDown.value)) {
      hide()
    } else if (
      target &&
      (props.clickIgnoreOn.length === 0 || !targetInOneOfSelectors(target as HTMLElement, props.clickIgnoreOn)) &&
      (props.clickRegisterOn.length === 0 || targetInOneOfSelectors(target as HTMLElement, props.clickRegisterOn))
    ) {
      show()
    }
  }
}

function onMouseenter() {
  hovering.value = true

  if (props.trigger === 'hover') {
    show()
  }
}

function onMouseleave() {
  hovering.value = false

  if (props.trigger === 'hover') {
    hide()
  }
}

async function show() {
  if (visible.value) return

  await delay(props.openDelay)

  if ((props.trigger === 'hover' && !hovering.value) || props.trigger === 'disabled') {
    return
  }

  destroyPopper()
  rendered.value = true
  visible.value = true
  active.value = true
  await nextTick()

  if (!popper.value || !reference.value) {
    return
  }

  popperInstance = createPopper(reference.value, popper.value, {
    strategy: 'fixed',
    placement: props.placement,
    modifiers: modifiers.value,
  })

  document.addEventListener('keydown', onKeyDown)

  emit('show')
}

function hide() {
  if (props.transition === '') {
    destroyPopper()
  } else {
    visible.value = false
    active.value = false
  }

  if (props.trigger === 'click' || props.trigger === 'focus') {
    document.addEventListener('keydown', onKeyDown)
  }

  emit('hide')
}

function destroyPopper() {
  visible.value = false
  active.value = false
  if (!popperInstance) return

  popperInstance.destroy()
  popperInstance = undefined
  emit('destroyPopper')
}

async function updatePopper() {
  return await popperInstance?.update()
}

useResizeObserver(popper, updatePopper)

defineExpose({
  updatePopper,
  wrapper,
  popper,
  reference,
  hovering,
  visible,
  show,
  hide,
})
</script>

<script lang="ts">
type CustomMouseEvent = MouseEvent & { isStopped?: boolean }

let popperContainer = document.getElementById('v-poppers')

if (!popperContainer) {
  popperContainer = document.createElement('div')
  popperContainer.id = 'v-poppers'
  document.body.appendChild(popperContainer)
}

let counter = 0

const mouseupListeners: Record<number, (event: CustomMouseEvent) => void> = {}

document.addEventListener('mouseup', (event: CustomMouseEvent) => {
  const listener = Object.values(mouseupListeners).at(-1)
  if (listener) {
    listener(event)
  }
})
</script>

<style>
.v-popper {
  z-index: 2000;
}

.v-popper--hidden {
  visibility: hidden;
}

.v-popper-reference {
  position: relative;

  &:focus {
    outline: none;
  }
}

.v-popper__arrow::after {
  content: '';
  background-color: #fff;
  top: -5px;
  left: 0px;
  width: 8px;
  height: 8px;
  position: absolute;
  transform: translateX(-50%) rotate(45deg);
}

.v-popper[data-popper-placement^='top'] .v-popper__arrow {
  bottom: 0;
}

.v-popper[data-popper-placement^='bottom'] .v-popper__arrow {
  top: 0;
}

.v-popper[data-popper-placement^='left'] .v-popper__arrow {
  right: 0;
}

.v-popper[data-popper-placement^='right'] .v-popper__arrow {
  left: 0;
}
</style>
